import { apiClient } from '../utils/api-client';

export interface User {
  id: string;
  user_id?: number;
  username: string;
  slug: string;
  name: string;
  surname: string;
  full_name: string;
  email: string;
  coach?: boolean;
  /** Invited by address and not signed up yet: no username, only the email. */
  invitation_pending?: boolean;
  avatar_url?: string | null;
  avatar_small_url?: string | null;
}

export interface Staff {
  id: string;
  user: User;
  location: {
    id: string;
    name: string;
    slug: string;
  };
  staff_roles: string[];
  status: 'pending' | 'confirmed' | 'rejected';
  can_confirm: boolean;
  can_reject: boolean;
  can_update: boolean;
}

export type StaffRole = 'admin' | 'editor' | 'judge' | 'routesetter' | 'member_manager';

export interface StaffResponse {
  data: {
    id: string;
    type: 'staff';
    attributes: Staff;
  };
}

export interface StaffListResponse {
  data: Array<{
    id: string;
    type: 'staff';
    attributes: Staff;
  }>;
}

/**
 * Somebody already on DivePeaks, picked from the search — or an address, for
 * somebody who may not be: the server invites whoever answers to it, and opens
 * an account for them to finish when nobody does.
 */
export type CreateStaffRequest =
  | { staff_roles: string[]; user_slug: string }
  | { staff_roles: string[]; email: string; name?: string; surname?: string };

/**
 * The API renders staff JSON:API-style: the record id lives at the top level of
 * the resource object, not inside `attributes`. Merge it back in so callers get
 * a `Staff` with a real `id` — the Staff page keys its list and matches the
 * coach toggle by it, and every row would toggle at once without it.
 */
function toStaff(item: { id: string; attributes: Staff }): Staff {
  return { ...item.attributes, id: item.id ?? item.attributes?.id };
}

class StaffService {
  async getLocationStaff(locationId: string, role?: StaffRole): Promise<Staff[]> {
    const url = role
      ? `/api/locations/${locationId}/staff?role=${encodeURIComponent(role)}`
      : `/api/locations/${locationId}/staff`;
    const response = await apiClient.get(url, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to fetch location staff');
    }

    const data: StaffListResponse = await response.json();
    return data.data.map(toStaff);
  }

  async getLocationRoutesetters(locationId: string): Promise<Staff[]> {
    return this.getLocationStaff(locationId, 'routesetter');
  }

  async getCurrentUserStaff(locationId: string): Promise<Staff> {
    const response = await apiClient.get(`/api/locations/${locationId}/staff/me`, { requireAuth: true });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Staff record not found');
      }
      throw new Error('Failed to fetch current user staff');
    }

    const data: StaffResponse = await response.json();
    return toStaff(data.data);
  }

  async createStaff(locationId: string, staffData: CreateStaffRequest): Promise<Staff> {
    const response = await apiClient.post(`/api/locations/${locationId}/staff`, {
      staff: staffData
    }, { requireAuth: true });

    if (!response.ok) {
      // Error toast will be shown automatically by apiClient
      throw new Error('Failed to create staff invitation');
    }

    const data: StaffResponse = await response.json();
    return toStaff(data.data);
  }

  async confirmInvitation(locationId: string): Promise<Staff> {
    const response = await apiClient.post(`/api/locations/${locationId}/staff/me/confirm`, {}, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to confirm invitation');
    }

    const data: StaffResponse = await response.json();
    return toStaff(data.data);
  }

  async rejectInvitation(locationId: string): Promise<Staff> {
    const response = await apiClient.post(`/api/locations/${locationId}/staff/me/reject`, {}, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to reject invitation');
    }

    const data: StaffResponse = await response.json();
    return toStaff(data.data);
  }

  async setStaffCoach(locationId: string, userSlug: string, coach: boolean): Promise<Staff> {
    const response = await apiClient.patch(`/api/locations/${locationId}/staff/${userSlug}`, {
      staff: { coach },
    }, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to update coach role');
    }

    const data: StaffResponse = await response.json();
    return toStaff(data.data);
  }

  /** Replaces the roles a staff member holds here — answered invitation or not. */
  async setStaffRoles(locationId: string, userSlug: string, roles: string[]): Promise<Staff> {
    const response = await apiClient.patch(`/api/locations/${locationId}/staff/${userSlug}`, {
      staff: { staff_roles: roles },
    }, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to update staff roles');
    }

    const data: StaffResponse = await response.json();
    return toStaff(data.data);
  }

  async removeStaff(locationId: string, userSlug: string): Promise<void> {
    const response = await apiClient.delete(`/api/locations/${locationId}/staff/${userSlug}`, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to remove staff member');
    }
  }

  async leaveStaff(locationId: string): Promise<void> {
    const response = await apiClient.delete(`/api/locations/${locationId}/staff/me`, { requireAuth: true });

    if (!response.ok) {
      throw new Error('Failed to leave staff');
    }
  }

  async searchUsers(query: string): Promise<User[]> {
    const response = await apiClient.get(`/api/search?type=user&q=${encodeURIComponent(query)}`);

    if (!response.ok) {
      throw new Error('Failed to search users');
    }

    const data = await response.json();
    return data.data?.map((item: Record<string, unknown>) => item.attributes) || [];
  }
}

export const staffService = new StaffService();
